import { Card, Skeleton, Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { budgetFormat } from "../../../utils/number/budget-format.ts"
import { brand, chipVariants, iconTileTints, neutral, shadows } from "../tokens"
import type { ChipVariant } from "../tokens"
import type {
  KpiCompare,
  KpiTile,
  KpiTrend,
  KpiUnit,
  WidgetProps
} from "../types"
import { WidgetFrame } from "../WidgetFrame"
import { getKpiIconEntry } from "./kpiIconMap"

const fmt = (t: KpiTile, currency: string): string => {
  const n = Number(t.value)
  if (!Number.isFinite(n)) return `${t.value}`
  if (t.unit === "percent") return `${n.toFixed(1)}%`
  if (t.unit === "currency") return budgetFormat(n, { currency })
  return `${t.value}`
}

function trendChipVariant(direction: "up" | "down" | "flat"): ChipVariant {
  if (direction === "up") return "ok"
  if (direction === "down") return "err"
  return "muted"
}

type Direction = "up" | "down" | "flat"

const directionOf = (basis: number): Direction => {
  if (basis > 0) return "up"
  if (basis < 0) return "down"
  return "flat"
}

const DIRECTION_ARROW: Record<Direction, string> = {
  up: "▲",
  down: "▼",
  flat: "—"
}

const VALUE_FONT_SIZE = 27
const VALUE_MIN_FONT_SIZE = 13
// Two tiles per row on a phone leaves the value ~94px, well short of what a long
// currency reading needs at 27px, so size it off the row's own inline size and the
// character count — a formatted number averages ~0.57em per glyph.
const VALUE_EM_PER_CHAR = 1.75
const VALUE_FONT_SIZE_CSS = `clamp(${VALUE_MIN_FONT_SIZE}px, calc(100cqi / var(--kpi-value-length) * ${VALUE_EM_PER_CHAR}), ${VALUE_FONT_SIZE}px)`
const KPI_BODY_CLASS = "p-3"
const KPI_ROW_GAP = 10
const KPI_COPY_GAP = 4
const DELTA_CHIP_FONT_SIZE = 11
const DELTA_CHIP_LINE_HEIGHT = 1.25
const DELTA_CHIP_VERTICAL_PADDING = 1
// Reserve the value row and the delta row at their tallest variant so a tile that
// swaps its value + chip for the shorter "no data" line keeps the same height and
// the sections below it don't reflow upward.
const DELTA_SLOT_MIN_HEIGHT = Math.ceil(
  DELTA_CHIP_FONT_SIZE * DELTA_CHIP_LINE_HEIGHT + DELTA_CHIP_VERTICAL_PADDING * 2
)

const SCALE_TRACK_HEIGHT = 5
const SCALE_MAX = 100

// Percent tiles are the only ones whose scale is knowable client-side: 0..100 is
// the whole range, so the bar needs no target from the payload. Readings outside
// it (an over-budget health figure) clamp — the value text carries the real number.
export function percentScaleValue(tile: KpiTile): number | null {
  if (tile.unit !== "percent") return null
  const n = Number(tile.value)
  if (!Number.isFinite(n)) return null
  return Math.max(0, Math.min(SCALE_MAX, n))
}

// When the backend cannot express a percentage delta (a base of zero), fall back
// to the absolute delta formatted in the tile's own unit.
export function describeKpiCompare(
  compare: KpiCompare,
  unit: KpiUnit | undefined,
  currency: string
): { direction: Direction; text: string } {
  const basis = compare.delta_pct ?? compare.delta
  const direction = directionOf(basis)
  if (compare.delta_pct !== null) {
    return { direction, text: `${Math.abs(compare.delta_pct).toFixed(1)}%` }
  }
  const magnitude = Math.abs(compare.delta)
  const text =
    unit === "currency"
      ? budgetFormat(magnitude, { currency })
      : unit === "percent"
        ? `${magnitude.toFixed(1)}%`
        : `${magnitude}`
  return { direction, text }
}

// A compare badge and the built-in trend chip can express the same delta (e.g.
// both "▼50.6%"). When a report tile carries a compare block, the richer compare
// badge wins and the duplicate trend chip is suppressed. Dashboard tiles never
// receive a compare block, so their trend chip always renders.
export function trendDuplicatesCompare(
  trend: KpiTrend | null | undefined,
  compare: KpiCompare | null | undefined
): boolean {
  if (!trend || !compare || compare.delta_pct === null) return false
  if (directionOf(compare.delta_pct) !== trend.direction) return false
  return Math.abs(trend.pct).toFixed(1) === Math.abs(compare.delta_pct).toFixed(1)
}

const CompareBadge = ({
  compare,
  unit,
  currency
}: {
  compare: KpiCompare
  unit: KpiUnit | undefined
  currency: string
}) => {
  const { direction, text } = describeKpiCompare(compare, unit, currency)
  const variant = trendChipVariant(direction)
  return (
    <span
      data-testid="kpi-compare-badge"
      data-direction={direction}
      style={{
        alignSelf: "flex-start",
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        fontSize: 11,
        fontWeight: 700,
        height: DELTA_SLOT_MIN_HEIGHT,
        padding: "0 8px",
        borderRadius: 999,
        background: chipVariants[variant].bg,
        color: chipVariants[variant].fg
      }}
    >
      {DIRECTION_ARROW[direction]} {text}
    </span>
  )
}

const InfoIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </svg>
)

type Translate = (key: string, defaultValue: string) => string

/**
 * A named cause beats a generic absence: the client read "0.0%" as an overspend
 * and "100.0%" as a perfect margin when both meant an input was never entered.
 * Only a period metric can be empty *for the window* — blaming the date filter
 * on the as-of-today tiles is what made the grid look broken.
 */
export function emptyStateText(tile: KpiTile, t: Translate): string {
  switch (tile.empty_reason) {
    case "no_costs":
      return t("portfolio.tile.emptyNoCosts", "No costs recorded yet")
    case "costs_unpaid":
      return t("portfolio.tile.emptyCostsUnpaid", "No costs paid yet")
    case "no_revenue":
      return t("portfolio.tile.emptyNoRevenue", "No revenue recorded yet")
    case "revenue_unpaid":
      return t("portfolio.tile.emptyRevenueUnpaid", "No payments received yet")
    case "no_budget":
      return t("portfolio.tile.emptyNoBudget", "No budget set")
    case "no_finished_milestones":
      return t("portfolio.tile.emptyNoFinished", "No milestones finished yet")
    case "no_contract_value":
      return t("portfolio.tile.emptyNoContractValue", "No sales price set on any project")
    case "no_contract_expenses":
      return t("portfolio.tile.emptyNoContractExpenses", "No planned expenses set on any project")
    case "no_active_milestones":
      return t("portfolio.tile.emptyNoActiveMilestones", "No active projects to measure")
    case "no_scheduled_milestones":
      return t("portfolio.tile.emptyNoScheduled", "No milestones scheduled")
    case "no_planned_income":
      return t("portfolio.tile.emptyNoPlannedIncome", "No income planned on any milestone")
    case "no_planned_cost":
      return t("portfolio.tile.emptyNoPlannedCost", "No cost planned on any milestone")
    case "nothing_billed":
      return t("portfolio.tile.emptyNothingBilled", "Nothing invoiced yet")
    default:
      // An unknown basis keeps the long-standing copy, which is already
      // translated — a client ahead of its backend must not silently
      // downgrade every empty tile to a new, untranslated string.
      return tile.basis === "snapshot"
        ? t("portfolio.tile.emptySnapshot", "No data yet")
        : t("portfolio.tile.empty", "No data for this window")
  }
}

export function KpiExplanation({ tile }: { tile: KpiTile }) {
  const { t } = useTranslation()
  const ignored = tile.filters_ignored ?? []
  if (!tile.description && !tile.formula && !ignored.length) return null

  const dimensionLabels = ignored.map((dimension) =>
    dimension === "project"
      ? t("portfolio.tile.dimension.project", "project")
      : t("portfolio.tile.dimension.projectStatus", "project status")
  )

  return (
    // The popup is a React portal, so its events bubble through the React tree
    // to the Card's onClick. Without this, selecting the formula text navigates
    // away from the page you are reading it on.
    <div
      onClick={(event) => event.stopPropagation()}
      style={{ display: "flex", flexDirection: "column", gap: 6, maxWidth: 260 }}
    >
      {tile.description ? <span>{tile.description}</span> : null}
      {tile.formula ? (
        <span style={{ opacity: 0.85 }}>
          {t("portfolio.tile.formula", "Calculated as")}: {tile.formula}
        </span>
      ) : null}
      {/* Silence beats a guess: an absent basis is not evidence of a snapshot. */}
      {tile.basis ? (
        <span style={{ opacity: 0.85 }}>
          {tile.basis === "period"
            ? t("portfolio.tile.basisPeriod", "Covers the selected date range.")
            : t(
                "portfolio.tile.basisSnapshot",
                "Reads as of today — the date filter does not change it."
              )}
        </span>
      ) : null}
      {dimensionLabels.length ? (
        <span style={{ opacity: 0.85 }}>
          {t(
            "portfolio.tile.filtersIgnored",
            "Includes data your {{dimensions}} filter cannot narrow.",
            { dimensions: dimensionLabels.join(" / ") }
          )}
        </span>
      ) : null}
    </div>
  )
}

const KpiCardImpl = ({
  tile,
  loading,
  currency = "USD",
}: {
  tile: KpiTile
  loading?: boolean
  currency?: string
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const trend = tile.trend ?? null
  const compare = tile.compare ?? null
  const noData = tile.has_data === false
  const valueText = fmt(tile, currency)
  const showTrend = trend != null && !trendDuplicatesCompare(trend, compare)
  const clickable = Boolean(tile.drill_to)
  const scaleValue = percentScaleValue(tile)
  const { icon: IconComponent, tint } = getKpiIconEntry(tile)
  const tileTint = iconTileTints[tint]
  const portfolioWide = Boolean(tile.filters_ignored?.length)
  const explanation = <KpiExplanation tile={tile} />
  const hasExplanation = Boolean(tile.description || tile.formula || portfolioWide)

  if (loading) {
    return (
      <Card
        data-testid="kpi-card"
        style={{
          border: `1px solid ${neutral.border}`,
          borderRadius: 12,
          boxShadow: shadows.card,
          height: "100%",
        }}
        classNames={{ body: KPI_BODY_CLASS }}
      >
        <Skeleton.Node active style={{ width: "100%", height: 56 }} />
      </Card>
    )
  }

  return (
    <Card
      data-testid="kpi-card"
      hoverable={clickable}
      onClick={clickable ? () => navigate(tile.drill_to) : undefined}
      style={{
        border: `1px solid ${neutral.border}`,
        borderRadius: 12,
        boxShadow: shadows.card,
        // A tile is only as tall as its own copy; in a grid row that leaves
        // ragged bottoms, so it takes whatever height the row gives it.
        height: "100%",
        cursor: clickable ? undefined : "default",
      }}
      classNames={{ body: KPI_BODY_CLASS }}
    >
      <div
        data-testid="kpi-content"
        style={{ display: "flex", gap: KPI_ROW_GAP, alignItems: "flex-start" }}
      >
        <div
          data-tint={tint}
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            flex: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: tileTint.bg,
            color: tileTint.fg,
          }}
          aria-hidden
        >
          <IconComponent
            width={17}
            height={17}
            style={{ display: "block" }}
          />
        </div>
        <div
          data-testid="kpi-copy"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: KPI_COPY_GAP,
            flex: 1,
            minWidth: 0
          }}
        >
          {/* The chip rides the title row rather than taking one of its own: a
              conditional row would make chipped and unchipped cards different
              heights, which the reserved slots below exist to prevent. */}
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              color: neutral.muted,
            }}
          >
            <span
              style={{
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {tile.title}
            </span>
            {hasExplanation ? (
              <Tooltip title={explanation} placement="top" trigger={["hover", "focus"]}>
                {/* A button, not a span: antd opens on focus, and only a
                    focusable element can receive it. Mouse-only would leave the
                    explanation unreachable for the keyboard and screen readers
                    this tooltip was added for. */}
                <button
                  type="button"
                  data-testid="kpi-info"
                  aria-label={t("portfolio.tile.infoLabel", "How this is calculated")}
                  onClick={(event) => event.stopPropagation()}
                  style={{
                    display: "inline-flex",
                    flex: "none",
                    cursor: "help",
                    padding: 0,
                    border: "none",
                    background: "none",
                    color: "inherit",
                    lineHeight: 0,
                  }}
                >
                  <InfoIcon width={13} height={13} style={{ display: "block" }} />
                </button>
              </Tooltip>
            ) : null}
            {portfolioWide ? (
              <span
                data-testid="kpi-portfolio-wide"
                style={{
                  flex: "none",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  padding: "1px 6px",
                  borderRadius: 999,
                  whiteSpace: "nowrap",
                  background: chipVariants.muted.bg,
                  color: chipVariants.muted.fg,
                }}
              >
                {t("portfolio.tile.portfolioWide", "Portfolio-wide")}
              </span>
            ) : null}
          </span>
          <div
            data-testid="kpi-value-row"
            style={{
              minHeight: VALUE_FONT_SIZE,
              display: "flex",
              alignItems: "center",
              containerType: "inline-size",
              minWidth: 0
            }}
          >
            {noData ? (
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  lineHeight: 1.3,
                  color: neutral.muted,
                }}
              >
                {emptyStateText(tile, t)}
              </span>
            ) : (
              <span
                data-testid="kpi-value"
                title={valueText}
                style={{
                  "--kpi-value-length": Math.max(valueText.length, 1),
                  fontSize: VALUE_FONT_SIZE_CSS,
                  fontWeight: 800,
                  lineHeight: 1,
                  letterSpacing: "-0.02em",
                  fontFamily: "Manrope, system-ui, sans-serif",
                  color: neutral.ink,
                  maxWidth: "100%",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                } as React.CSSProperties}
              >
                {valueText}
              </span>
            )}
          </div>
          {!noData && tile.sample !== undefined ? (
            <span
              data-testid="kpi-sample"
              style={{ fontSize: 11, lineHeight: 1.3, color: neutral.faint }}
            >
              {t("portfolio.tile.sample", "based on {{count}}", { count: tile.sample })}
            </span>
          ) : null}
          {!noData && scaleValue !== null ? (
            <div
              data-testid="kpi-scale"
              data-scale-value={scaleValue}
              aria-hidden
              style={{
                height: SCALE_TRACK_HEIGHT,
                borderRadius: 999,
                background: neutral.light,
                overflow: "hidden"
              }}
            >
              <div
                style={{
                  width: `${scaleValue}%`,
                  height: "100%",
                  borderRadius: 999,
                  background: brand.teal
                }}
              />
            </div>
          ) : null}
          <div
            data-testid="kpi-delta-slot"
            style={{
              minHeight: DELTA_SLOT_MIN_HEIGHT,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 6
            }}
          >
            {!noData && showTrend && trend ? (
              <span
                data-testid="kpi-trend-chip"
                data-chip-variant={trendChipVariant(trend.direction)}
                style={{
                  alignSelf: "flex-start",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  fontSize: DELTA_CHIP_FONT_SIZE,
                  lineHeight: DELTA_CHIP_LINE_HEIGHT,
                  fontWeight: 700,
                  height: DELTA_SLOT_MIN_HEIGHT,
                  padding: "0 8px",
                  borderRadius: 999,
                  background: chipVariants[trendChipVariant(trend.direction)].bg,
                  color: chipVariants[trendChipVariant(trend.direction)].fg,
                }}
              >
                {trend.direction === "up" ? "▲" : trend.direction === "down" ? "▼" : "—"}{" "}
                {Math.abs(trend.pct)}%
              </span>
            ) : null}
            {!noData && compare ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  flexWrap: "wrap",
                  minHeight: DELTA_SLOT_MIN_HEIGHT
                }}
              >
                <CompareBadge
                  compare={compare}
                  unit={tile.unit}
                  currency={currency}
                />
                {tile.compareLabel ? (
                  <span
                    data-testid="kpi-compare-label"
                    style={{ fontSize: 11, color: neutral.muted }}
                  >
                    {t("portfolio.tile.vs", "vs {{label}}", {
                      label: tile.compareLabel
                    })}
                  </span>
                ) : null}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </Card>
  )
}

export const KpiCard = React.memo(KpiCardImpl)

const LOADING_PLACEHOLDER: KpiTile = { id: "", title: "", value: "—", drill_to: "" }

export const KpiWidget = ({ payload, state, currency }: WidgetProps) => {
  const tile = payload?.tile ?? LOADING_PLACEHOLDER
  const loading = state === "loading" || !payload?.tile
  return (
    <WidgetFrame bare>
      <KpiCard tile={tile} loading={loading} currency={payload?.currency ?? currency} />
    </WidgetFrame>
  )
}
